import React, { createContext, useContext, useState } from "react";
import checkoutServices from "~/services/checkoutServices";
import _ from "lodash";
import { DialogBuilder } from "~/utils/DialogProvider";
import { useTranslation } from "react-i18next";
import { retryRequest } from "~/utils/retryUtils";

const PolicyCancellationContext = createContext();

export const PolicyCancellationProvider = ({ children }) => {
	const [ policyCancellation, setPolicyCancellation ] = useState(null);
	
	const dialog = useContext(DialogBuilder);
	const { t } = useTranslation();

	const validateAdvancedBilling = (policyCancellation) => {
		if (!policyCancellation?.cancellation_policy) return;

		return !!(
			policyCancellation.cancellation_policy.fixed_cancellation ||
			policyCancellation.cancellation_policy.percentage_cancellation ||
			policyCancellation.cancellation_policy.anticipated_payment
		);
	};

	const loadPolicyCancellation = async (businessId, businessLanguage) => {
		try {
			const { data } = await retryRequest(
				checkoutServices.fetchCancellationPolicyScreen,
				[businessId, businessLanguage],
				3,
				1000, 
				"Policy Cancellation"
			);
			
			setPolicyCancellation(data);
			let isPolicyCancellationActive = validateAdvancedBilling(data);
			localStorage.setItem(`@prit-web/policyCancellation/${businessId}`, JSON.stringify({ isPolicyCancellationActive }));
			return {
				business_id: businessId,
				isPolicyCancellation: isPolicyCancellationActive,
				data,
			};
		} catch (error) {
			console.error("All retry attempts failed. Showing error dialog.");
			if (dialog && t) {
				dialog
					.withTitle("Error")
					.withMessage(t("Error loading cancellation policy"))
					.withButtonText(t("Try again"))
					.withAction(() => {
						localStorage.removeItem(`@prit-web/policyCancellation/${businessId}`);
						loadPolicyCancellation(businessId, businessLanguage);
					})
					.show();
			}
		}
	};

	const getPolicyCancellation = (businessId) => {
		const key = businessId
			? `@prit-web/policyCancellation/${businessId}`
			: "@prit-web/policyCancellation";
		const savedPolicyCancellation = localStorage.getItem(key);
		if (!_.isEmpty(savedPolicyCancellation)) {
			return JSON.parse(savedPolicyCancellation);
		}
		return policyCancellation;
	};

	return (
		<PolicyCancellationContext.Provider
			value={{ policyCancellation, loadPolicyCancellation, validateAdvancedBilling, getPolicyCancellation }}>
			{children}
		</PolicyCancellationContext.Provider>
	);
};

export const usePolicyCancellation = () => {
	const context = useContext(PolicyCancellationContext);
	if (_.isEmpty(context)) {
		throw new Error("usePolicyCancellation must be used within a PolicyCancellationProvider");
	}
	return context;
};
